import { useState } from "react";
import {
  Send,
  FileText,
  ClipboardList,
  AlertCircle,
  Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import type { Tables } from "@/types/database";
import { getDocumentsSentRequirements } from "@/utils/workflowValidation";
import { useUpdateEvent } from "@/hooks/useEvents";
import { useOrganizationSettings } from "@/hooks/useOrganizationSettings";
import { useToast } from "@/hooks/useToast";

type Event = Tables<"events"> & {
  quote?: Tables<"quotes"> | null;
  technical_sheet?: Tables<"technical_sheets"> | null;
  organizer?: Tables<"organizers"> | null;
};

interface SendDocumentsDialogProps {
  open: boolean;
  onClose: () => void;
  event: Event;
}

export function SendDocumentsDialog({
  open,
  onClose,
  event,
}: SendDocumentsDialogProps) {
  const [isSending, setIsSending] = useState(false);
  const updateEvent = useUpdateEvent();
  const { data: settings } = useOrganizationSettings();
  const { toast } = useToast();

  const requirements = getDocumentsSentRequirements(event);
  const canSend = requirements.canSend;
  const recipientEmail = event.organizer?.email;

  const handleSendDocuments = async () => {
    if (!canSend || !recipientEmail) return;

    setIsSending(true);

    try {
      const quoteHtml = await fetch("/.netlify/functions/generateQuotePdf", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          event: {
            name: event.name,
            event_date: event.event_date,
            event_time: event.event_time,
            location: event.location,
          },
          organization: {
            name: event.organizer?.name ?? "Organisation",
            address: event.organizer?.address,
            email: event.organizer?.email,
            phone: event.organizer?.phone,
          },
          quote: event.quote,
          settings: settings,
        }),
      }).then((r) => r.text());

      const technicalSheetHtml = await fetch(
        "/.netlify/functions/generateTechnicalSheetPdf",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            event: {
              name: event.name,
              event_date: event.event_date,
              location: event.location,
            },
            technicalSheet: event.technical_sheet,
          }),
        },
      ).then((r) => r.text());

      const emailContent = `
        <h2>Documents pour votre événement: ${event.name}</h2>
        <p>Bonjour,</p>
        <p>Suite à nos échanges, veuillez trouver ci-dessous les documents pour votre événement:</p>
        <ol>
          <li><strong>Le devis détaillé</strong></li>
          <li><strong>La fiche technique complète</strong></li>
        </ol>
        <p>Prochaines étapes:</p>
        <ol>
          <li>Signez le devis</li>
          <li>Versez l'acompte de 30%</li>
          <li>L'événement sera officiellement confirmé</li>
        </ol>
        <hr/>
        <h3>DEVIS</h3>
        ${quoteHtml}
        <hr/>
        <h3>FICHE TECHNIQUE</h3>
        ${technicalSheetHtml}
        <hr/>
        <p>Pour toute question, n'hésitez pas à nous contacter.</p>
        <p>Cordialement,<br/>Collectif Béatitudes Musique</p>
      `;

      await fetch("/.netlify/functions/sendEmail", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: recipientEmail,
          subject: `Documents - ${event.name} - Collectif Béatitudes`,
          htmlContent: emailContent,
        }),
      });

      await updateEvent.mutateAsync({
        id: event.id,
        updates: { status: "Documents Sent" },
      });

      toast({
        title: "Documents envoyés",
        description: `Email envoyé à ${recipientEmail}`,
      });

      onClose();
    } catch (error) {
      console.error("Error sending documents:", error);
      toast({
        title: "Erreur d'envoi",
        description: "Une erreur est survenue lors de l'envoi des documents",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Envoyer les documents
          </DialogTitle>
          <DialogDescription>
            Le devis et la fiche technique seront envoyés ensemble au client.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              {requirements.hasQuote ? (
                <Check className="h-5 w-5 text-green-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-amber-500" />
              )}
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                <span>Devis</span>
              </div>
              {requirements.hasQuote ? (
                <span className="text-sm text-green-600 ml-auto">Prêt</span>
              ) : (
                <span className="text-sm text-amber-500 ml-auto">À créer</span>
              )}
            </div>

            <div className="flex items-center gap-2">
              {requirements.hasTechnicalSheet ? (
                <Check className="h-5 w-5 text-green-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-amber-500" />
              )}
              <div className="flex items-center gap-2">
                <ClipboardList className="h-4 w-4" />
                <span>Fiche technique</span>
              </div>
              {requirements.hasTechnicalSheet ? (
                <span className="text-sm text-green-600 ml-auto">Prête</span>
              ) : (
                <span className="text-sm text-amber-500 ml-auto">À créer</span>
              )}
            </div>
          </div>

          {!canSend && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Veuillez créer le devis et la fiche technique avant d'envoyer
                les documents.
              </AlertDescription>
            </Alert>
          )}

          {canSend && !recipientEmail && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                L'organisateur n'a pas d'adresse email configurée.
              </AlertDescription>
            </Alert>
          )}

          {canSend && recipientEmail && (
            <div className="rounded-lg border p-3 bg-muted/50">
              <p className="text-sm text-muted-foreground">Destinataire</p>
              <p className="font-medium">{recipientEmail}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button
            onClick={handleSendDocuments}
            disabled={!canSend || !recipientEmail || isSending}
          >
            {isSending ? (
              "Envoi en cours..."
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Envoyer les documents
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
